import React, { useEffect, useMemo, useRef, useState } from 'react';
import { STRATA, MATCHES, tintOf, loadSchemaInfo } from '../nucleus/GraphModel';
import { bucketsOf, ymd } from './nodeFacets';
import { dayOf, fmt } from '../../nucleus/format';
import Segmented from '../../components/nucleus/Segmented';
import Picker from '../../components/nucleus/Picker';
import { Switch } from '../../components/nucleus/Switch';
import { Facet, RailHead, RailSection } from '../../components/nucleus/FacetRail';

// THE FILTER RAIL — the graph's left edge: every way to narrow the nodes, each
// a histogram of what it would show. A bar's length is its count under the
// other filters; pressing a row picks it (another row adds to it), ⌥-press
// picks it alone. The timeline is the dates themselves: drag across it for a
// range, press a bar for that day, week or month. The rail's head, its parts
// and its facets are the shared ones (components/nucleus/FacetRail.js).
// Relationships are nodes too; a switch shows them among the rows (off at first).

const RANGES = [
    { id: 'any', word: 'Any time', short: 'Any' },
    { id: '1d', word: 'Past day', short: 'Day', days: 1 },
    { id: '7d', word: 'Past week', short: 'Week', days: 7 },
    { id: '30d', word: 'Past month', short: 'Month', days: 30 },
    { id: '1y', word: 'Past year', short: 'Year', days: 365 },
];
const UNIT_WORD = { day: 'day', week: 'week', month: 'month' };
const short = (d, unit) => (unit === 'month' ? d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' }) : dayOf(d));
// what a facet says when its counts did not come back (a 503, a timeout)
const FAILED = 'Counts didn’t load.';

export default function FilterRail({ group, filter, onFilter, search, kinds, facets, timeline, onClose, closeWord = 'Collapse' }) {
    const set = (patch) => onFilter({ ...filter, ...patch });
    const toggle = (list, k, only) => {
        if (only) return list.length === 1 && list[0] === k ? [] : [k];
        return list.includes(k) ? list.filter((x) => x !== k) : [...list, k];
    };
    // the search counts as a filter too: it narrows the rows like any other
    const active = [
        search.trim(), filter.access !== 'owned' && !group, kinds.selected.length, filter.apps.length, filter.rels.length,
        filter.strata.length, filter.after || filter.before, filter.field && filter.value,
    ].filter(Boolean).length;
    const failed = !!facets?.failed;

    return (
        <section className="gx-rail" aria-label="Filter the graph">
            {/* the rail's head: what is filtered (or that nothing is) and Clear, then the way to fold the rail away */}
            <RailHead active={active} onClear={() => onFilter(null)} onClose={onClose} closeWord={closeWord} />

            {!group && (
                <RailSection title="Whose">
                    <Segmented
                        label="Whose nodes" fill stacked value={filter.access} onChange={(id) => set({ access: id })}
                        options={[['owned', 'Mine', facets?.ownership?.owned], ['shared', 'Shared', facets?.ownership?.shared], ['all', 'Both', facets?.ownership?.all]]
                            .map(([id, word, n]) => ({ id, label: word, count: n != null ? fmt(n) : null }))}
                    />
                </RailSection>
            )}

            {/* the links among the rows, or not: a switch, not a filter (Clear leaves it) */}
            <section className="gx-sec is-row">
                <div className="gx-sec__head">
                    <label htmlFor="gx-links">Relationships</label>
                    <Switch id="gx-links" aria-label="Show relationships" checked={!!filter.links} onChange={(e) => set({ links: e.target.checked })} />
                </div>
            </section>

            <Timeline filter={filter} set={set} dates={timeline?.dates ?? null} failed={!!timeline?.failed} />

            <Facet
                title="Schema"
                items={(facets?.kinds || []).map((s) => ({ key: s.key, label: s.name, count: s.count, tint: tintOf(s.name) }))}
                selected={kinds.selected}
                onToggle={(k, only) => kinds.onChange(toggle(kinds.selected, k, only))}
                loading={!facets}
                none={failed ? FAILED : undefined}
            />
            <Facet
                title="Written by"
                items={(facets?.apps || []).map(([name, n]) => ({ key: name, label: name === '__none__' ? 'You' : name, count: n }))}
                selected={filter.apps}
                onToggle={(k, only) => set({ apps: toggle(filter.apps, k, only) })}
                loading={!facets}
                none={failed ? FAILED : 'Only you have written here.'}
            />
            <Facet
                title="Linked by"
                items={(facets?.rels || []).map(([type, n]) => ({ key: type, label: type.replace(/_/g, ' '), count: n }))}
                selected={filter.rels}
                onToggle={(k, only) => set({ rels: toggle(filter.rels, k, only) })}
                loading={!facets}
                none={failed ? FAILED : 'No links yet.'}
            />
            {!group && (
                <RailSection title="Stratum">
                    <Segmented
                        label="Stratum" multiple fill value={filter.strata} onChange={(strata) => set({ strata })}
                        options={STRATA.map((s) => ({ id: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
                    />
                </RailSection>
            )}
            <FieldFilter filter={filter} set={set} kind={kinds.selected.length === 1 && kinds.selected[0] !== 'none' ? kinds.selected[0] : null} />
        </section>
    );
}

/** The dates as bars: drag for a range, press for one bucket, or a preset. */
function Timeline({ filter, set, dates, failed = false }) {
    const { unit, buckets } = useMemo(() => bucketsOf(dates || []), [dates]);
    const max = Math.max(1, ...buckets.map((b) => b.count));
    const [drag, setDrag] = useState(null); // { a, b } bucket indexes while dragging
    const box = useRef(null);
    const idxAt = (x) => {
        const r = box.current.getBoundingClientRect();
        return Math.max(0, Math.min(buckets.length - 1, Math.floor(((x - r.left) / r.width) * buckets.length)));
    };
    useEffect(() => {
        if (!drag) return undefined;
        const move = (e) => setDrag((d) => ({ ...d, b: idxAt(e.clientX) }));
        const up = () => {
            setDrag((d) => {
                if (d) {
                    const lo = buckets[Math.min(d.a, d.b)];
                    const hi = buckets[Math.max(d.a, d.b)];
                    if (lo && hi) set({ range: 'pick', after: ymd(lo.start), before: ymd(new Date(+hi.end - 1)) });
                }
                return null;
            });
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
        return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    }); // re-bound while dragging: it reads the buckets

    // what is picked, as bucket indexes (a drag in progress shows over it)
    const after = filter.after ? new Date(filter.after.length > 10 ? filter.after : `${filter.after}T00:00:00`) : null;
    const before = filter.before ? new Date(`${filter.before}T23:59:59`) : null;
    const lit = (b, i) => {
        if (drag) return i >= Math.min(drag.a, drag.b) && i <= Math.max(drag.a, drag.b);
        if (!after && !before) return null;
        return (!after || b.end > after) && (!before || b.start <= before);
    };
    const preset = (r) => {
        if (r.id === 'any') return set({ range: 'any', after: '', before: '' });
        const from = new Date(Date.now() - r.days * 86400000);
        return set({ range: r.id, after: r.days === 1 ? from.toISOString() : ymd(from), before: '' });
    };
    const picked = filter.after || filter.before;
    const label = picked && filter.range === 'pick'
        ? `${filter.after ? short(after, unit) : '…'} – ${filter.before ? short(before, unit) : 'now'}`
        : RANGES.find((r) => r.id === filter.range)?.word;

    return (
        <RailSection
            title="When"
            aside={(
                <Segmented
                    label="Which date" size="sm" value={filter.dateField} onChange={(id) => set({ dateField: id })}
                    options={[{ id: 'updated_at', label: 'Changed' }, { id: 'created_at', label: 'Made' }]}
                />
            )}
        >
            <div
                ref={box}
                className={`gx-time${dates === null ? ' is-loading' : ''}`}
                role="img"
                aria-label={`${fmt(dates?.length || 0)} nodes by ${UNIT_WORD[unit]}`}
                onPointerDown={(e) => { if (!buckets.length) return; e.preventDefault(); const i = idxAt(e.clientX); setDrag({ a: i, b: i }); }}
            >
                {buckets.map((b, i) => {
                    const on = lit(b, i);
                    return (
                        <span key={+b.start} className={`gx-time__bar${on === true ? ' is-on' : on === false ? ' is-off' : ''}`}
                            style={{ '--h': `${b.count ? Math.max(6, (b.count / max) * 100) : 0}%` }}
                            title={`${short(b.start, unit)}${unit !== 'day' ? ` (${UNIT_WORD[unit]})` : ''}: ${fmt(b.count)}`} />
                    );
                })}
            </div>
            {buckets.length > 0 && (
                <div className="gx-time__axis" aria-hidden="true">
                    <span>{short(buckets[0].start, unit)}</span><span>{unit === 'day' ? 'today' : 'now'}</span>
                </div>
            )}
            {failed && <p className="gx-quiet is-error">The dates didn’t load.</p>}
            {/* one row of five: the short words fit the rail, the full ones are each part's title */}
            <Segmented
                label="Presets" size="sm" fill className="gx-ranges"
                value={(filter.range || 'any') === 'any' && picked ? null : filter.range || 'any'}
                onChange={(id) => preset(RANGES.find((r) => r.id === id))}
                options={RANGES.map((r) => ({ id: r.id, label: r.short, title: r.word }))}
            />
            {picked && (
                <p className="gx-picked">
                    <span>{label}</span>
                    <button type="button" className="nx-link" onClick={() => preset(RANGES[0])}>Clear</button>
                </p>
            )}
        </RailSection>
    );
}

/** A field's value, once one kind is picked (the route needs the kind to read its fields). */
function FieldFilter({ filter, set, kind }) {
    const [props, setProps] = useState(null);
    // the value is typed here and handed on once the typing pauses
    const [val, setVal] = useState(filter.value);
    useEffect(() => { setVal(filter.value); }, [filter.value]);
    useEffect(() => {
        if (val === filter.value) return undefined;
        const t = setTimeout(() => set({ value: val }), 350);
        return () => clearTimeout(t);
    }, [val]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        let live = true;
        setProps(null);
        if (kind) loadSchemaInfo(kind).then((s) => { if (live) setProps(Object.keys(s.properties || {}).filter((k) => !(s.encrypted || []).includes(k))); }).catch(() => { if (live) setProps([]); });
        return () => { live = false; };
    }, [kind]);
    if (!kind) return null;
    return (
        <RailSection title="Field" aside={filter.field && <button type="button" className="nx-link" onClick={() => set({ field: '', value: '' })}>Clear</button>}>
            {props && !props.length && <p className="gx-quiet">This kind has no readable fields.</p>}
            {props?.length > 0 && (
                <div className="gx-field">
                    <Picker fill label="Field" placeholder="A field…" value={filter.field} onChange={(field) => set({ field })}
                        options={props.map((p) => ({ value: p, label: p.replace(/_/g, ' ') }))} />
                    {filter.field && (
                        <>
                            <Picker fill label="Match" value={filter.match} onChange={(match) => set({ match })}
                                options={MATCHES.map((m) => ({ value: m.id, label: m.word }))} />
                            <input type="text" value={val} onChange={(e) => setVal(e.target.value)} placeholder="Value" aria-label="Value" />
                        </>
                    )}
                </div>
            )}
        </RailSection>
    );
}
